import React, { memo, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { createPortal } from 'react-dom';
import { Box, Grid, Typography } from '@mui/material';
import { NoTicketMain } from '../no-ticket';
import TicketContainer from '../ticket-container';
import TicketPage from '../../pages/tickets.page';
import TicketListPage from 'features/tickets/components/ticket-list/ticket-list';
import AnimatedLayout from 'components/layouts/animated-layout';
import useBreakpoint from 'hooks/use-breakpoint';
import NewTicket from 'features/tickets/components/new-ticket/new-ticket';
import TicketSearch from 'features/tickets/components/ticket-search';
import HelmetTabComponent from 'components/helmet-tab/helmet-tab';

type TicketItemProps = {
    routing?: string;
    headerActionsTargetId?: string;
};

const TicketItem = ({ routing, headerActionsTargetId }: TicketItemProps) => {
    const { ticketId } = useParams();
    const { lowerThan, biggerThan } = useBreakpoint();

    const [ticketListSearch, setTicketListSearch] = useState<any>({});
    const [ticktListPage, setTicketListPage] = useState<number>(1);
    const [headerActionsTarget, setHeaderActionsTarget] =
        useState<HTMLElement | null>(null);
    const isMainTicketPage = !routing;
    const isEmbedded = Boolean(routing);

    useEffect(() => {
        if (!headerActionsTargetId) {
            setHeaderActionsTarget(null);
            return;
        }

        setHeaderActionsTarget(document.getElementById(headerActionsTargetId));
    }, [headerActionsTargetId]);

    const ticketActions = (
        <Grid
            container
            spacing={isEmbedded ? 0 : 1}
            width={isEmbedded ? 'auto' : { xs: '100%', md: 'auto' }}
            justifyContent={{ xs: 'flex-end', md: 'flex-end' }}
            sx={{ flexWrap: 'wrap' }}>
            {!isEmbedded && (
                <TicketSearch
                    search={ticketListSearch}
                    onSearch={setTicketListSearch}
                    onPage={setTicketListPage}
                />
            )}
            <NewTicket
                routing={routing}
                buttonSx={
                    isEmbedded
                        ? {
                              width: { xs: 96, sm: 104 },
                              minWidth: { xs: 96, sm: 104 },
                              maxWidth: { xs: 96, sm: 104 },
                              height: 40,
                              minHeight: 40,
                              px: { xs: 0.75, sm: 1 },
                              py: 0,
                              fontSize: { xs: 10, sm: 10.75 },
                              lineHeight: 1,
                              whiteSpace: 'nowrap',
                              boxSizing: 'border-box',
                              justifyContent: 'center',
                              alignItems: 'center',
                          }
                        : undefined
                }
            />
        </Grid>
    );

    return (
        <Box
            sx={{
                height: '100%',
                minHeight: 0,
                display: 'flex',
                flexDirection: 'column',
                overflow: 'hidden',
            }}>
            <HelmetTabComponent
                title={
                    isEmbedded
                        ? 'ارتباط با پشتیبانی'
                        : ticketId
                          ? `مشاهده تیکت ${ticketId}`
                          : 'پشتیبانی'
                }
            />

            {headerActionsTarget &&
                headerActionsTargetId &&
                createPortal(ticketActions, headerActionsTarget)}

            {(!isEmbedded || !headerActionsTargetId) && (
                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: { xs: 'stretch', md: 'center' },
                        mt: isEmbedded ? 0 : 3,
                        mb: isEmbedded ? 1.25 : 2,
                        gap: 2,
                        flexDirection: { xs: 'column', md: 'row' },
                        flexShrink: 0,
                    }}>
                    {!isEmbedded && (
                        <Box>
                            <Typography
                                sx={{ color: 'text.primary', lineHeight: 1.4 }}
                                fontSize={22}
                                className="font-yekan-heavy">
                                {ticketId ? (
                                    <>
                                        مشاهده تیکت{' '}
                                        <Box
                                            component="span"
                                            sx={{
                                                fontFamily: 'IRANSans !important',
                                            }}>
                                            {ticketId}
                                        </Box>
                                    </>
                                ) : (
                                    'پشتیبانی'
                                )}
                            </Typography>
                        </Box>
                    )}

                    {ticketActions}
                </Box>
            )}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'hidden',
                    '& > div': {
                        height: '100%',
                    },
                }}>
                <AnimatedLayout>
                    <Box
                        sx={{
                            width: '100%',
                            height: '100%',
                            minHeight: 0,
                            display: 'flex',
                            flexDirection: { xs: 'column', lg: 'row' },
                            flexGrow: 1,
                            gap: { xs: 2, lg: 2 },
                            pb: isMainTicketPage ? 0 : 2,
                        }}>
                    {ticketId && lowerThan('md') ? null : (
                        <Box
                            sx={{
                                width: { xs: '100%', lg: '34%' },
                                minWidth: { xs: 0, lg: 340 },
                                maxWidth: { lg: 430 },
                                borderRadius: 3,
                                overflow: 'hidden',
                                height: '100%',
                            }}>
                            <TicketListPage
                                routing={routing}
                                search={ticketListSearch}
                                onSearch={setTicketListSearch}
                                page={ticktListPage}
                                onPage={setTicketListPage}
                            />
                        </Box>
                    )}

                    {ticketId ? (
                        <TicketContainer>
                            <TicketPage />
                        </TicketContainer>
                    ) : biggerThan('md') ? (
                        <NoTicketMain routing={routing} />
                    ) : null}
                    </Box>
                </AnimatedLayout>
            </Box>
        </Box>
    );
};

export default memo(TicketItem);
